'use client';

import { useMemo, useRef, useState } from 'react';

import { useAuthentication, UserGroups, getUserType } from '@ravago/shared/authentication';
import { getLogger } from '@ravago/shared/logging/client';
import { QuotationUtils, UserUtils } from '@ravago/shared/next-data-access/client';
import { UOM, UserPermissions } from '@ravago/shared/page-data/models/config';
import { PackagingGroup, ProductDetail } from '@ravago/shared/page-data/models/elements';
import { LeadTimeIndicator } from '@ravago/shared/page-data/models/elements/product-lead-time.model';
import { GuestRequestForQuotation, PreferredDeliveryTimeframe, VerifiedRequestForQuotation } from '@ravago/shared/page-data/models/features';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BoxItem } from '@ravago/shared/radiance/components/box/components/BoxItem/BoxItem';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { IconButton } from '@ravago/shared/radiance/components/button/components/IconButton/IconButton';
import { Flyout } from '@ravago/shared/radiance/components/flyout/components/Flyout/Flyout';
import { Spinner } from '@ravago/shared/radiance/components/spinner/components/Spinner/Spinner';
import { DotsStepper } from '@ravago/shared/radiance/components/stepper/components/DotsStepper/DotsStepper';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useBreakpoint } from '@ravago/shared/radiance/hooks/useBreakpoint/useBreakpoint';
import { useTranslations } from '@ravago/shared/translations/client';

import { useMutation } from '@tanstack/react-query';

import { ReadonlyURLSearchParams, usePathname, useSearchParams } from 'next/navigation';

import { usePathLanguageCode } from '../../../../../../hooks/use-path-locale';
import { TrackingEvents, TrackingUtils } from '../../../../../../utils/tracking/tracking.utils';
import { Link } from '../../../../../shared/link/link';
import { ProductFlyoutColorDetails } from '../../../../../shared/product-flyout/product-flyout-color-details';
import { ProductFlyoutProductTitle } from '../../../../../shared/product-flyout/product-flyout-product-title';
import { useRequestForQuotationColor } from '../../../hooks/use-request-for-quotation-color';
import { useRequestForQuotationState } from '../../../hooks/use-request-for-quotation-state';
import { useRequestForQuotationSteps } from '../../../hooks/use-request-for-quotation-steps';
import { RequestForQuotationStep, RfqStep } from '../../../models/request-for-quotation-step';

import { FormValue as AddressDetailsFormValue, RfqFlyoutStepAddressDetails } from './steps/rfq-flyout-step-address-details';
import { FormValue as ContactDetailsFormValue, RfqFlyoutStepContactDetails } from './steps/rfq-flyout-step-contact-details';
import { FormValue as DeliveryAreaFormValue, RfqFlyoutStepDeliveryArea } from './steps/rfq-flyout-step-delivery-area';
import { RfqFlyoutStepFinalMessage } from './steps/rfq-flyout-step-final-message';
import {
   FormValue as QuotationDetailsFormValue,
   LocalFormValue as LocalQuotationDetailsFormValue,
   RfqFlyoutStepQuotationDetails
} from './steps/rfq-flyout-step-quotation-details';
import { RfqFlyoutStepRecognizedEmail } from './steps/rfq-flyout-step-recognized-email';
import { FormValue as SummaryFormValue, RfqFlyoutStepSummary } from './steps/rfq-flyout-step-summary';
import { FormValue as VerifyEmailFormValue, RfqFlyoutStepVerifyEmail } from './steps/rfq-flyout-step-verify-email';

interface RfqColor {
   colorName: string;
   producerColorCode: string | undefined;
   desiredColor: string | undefined;
}

interface Props {
   open: boolean;
   productId: string;
   productDescription: string;
   isCustomerProduct: boolean;
   defaultProduct?: ProductDetail;
   color: RfqColor | undefined;
   leadTimeIndicators?: LeadTimeIndicator[];
   leadTimeEnabled?: boolean;
   channelUOMs?: UOM[];
   userPermissions: UserPermissions;
   from: 'PRODUCT_DETAILS' | 'MY_PRICES';
   type: 'QUOTATION' | 'QUICK_QUOTE';
   onClose: () => void;
   onComplete: () => void;
}

const getInitialQuotationDetailsFormValue = (
   searchParams: ReadonlyURLSearchParams,
   packagingGroups: PackagingGroup[]
): LocalQuotationDetailsFormValue | undefined => {
   const quantitiesFromSearchParams = searchParams.get('quantities');
   const packagingFromSearchParams = searchParams.get('packaging');
   const packagingDescriptionFromSearchParams = searchParams.get('packaging-description');

   if (!quantitiesFromSearchParams || !packagingFromSearchParams) return undefined;

   const defaultPackagingGroup = packagingGroups.find((packaging) => packaging.id.toString() === packagingFromSearchParams);

   const defaultQuantityBreaks = quantitiesFromSearchParams.split('-');
   const quantityBreakValues = defaultQuantityBreaks.map((quantity) => ({ value: quantity })).slice(0, 5); // Normally the length won't be more than 5, but just to be safe

   return {
      packaging: defaultPackagingGroup
         ? {
              id: defaultPackagingGroup.id.toString(),
              value: defaultPackagingGroup.description
           }
         : undefined,
      packagingDescription: packagingDescriptionFromSearchParams ?? '',
      quantityBreaks: quantityBreakValues
   };
};

const mapUomIdToUnit = (id: string | undefined): string => {
   if (!id) return 'kg';

   switch (id) {
      case 'imperial':
         return 'lb';
      case 'metric':
         return 'kg';
      default:
         return 'kg';
   }
};

const getDefaultChannelUomId = (uoms: UOM[] | undefined): string => {
   if (!uoms || uoms.length === 0) return 'kg';
   const defaultUom = uoms.find((u) => u.default);
   return (defaultUom ?? uoms[0]).id;
};

export const RequestForQuotationFlyout = ({
   open,
   productId,
   productDescription,
   isCustomerProduct,
   defaultProduct,
   color,
   leadTimeIndicators = [],
   leadTimeEnabled = false,
   channelUOMs = [],
   userPermissions,
   from,
   type,
   onClose,
   onComplete
}: Props) => {
   const languageCode = usePathLanguageCode();
   const pathname = usePathname();
   const searchParams = useSearchParams();
   const translate = useTranslations();
   const { user, login } = useAuthentication();
   const { phone } = useBreakpoint();

   const { colors, packagingGroups, customer, countries, loading } = useRequestForQuotationState({
      productId,
      defaultProduct,
      isCustomerProduct
   });

   const {
      currentStep,
      setCurrentStep,
      currentStepIndex,
      nextStep,
      previousStep,
      reset: resetSteps,
      stepCount
   } = useRequestForQuotationSteps({ userPermissions });

   const requestQuotationMutation = useMutation({
      mutationFn: async (rfqData: VerifiedRequestForQuotation | GuestRequestForQuotation) =>
         'type' in rfqData ? QuotationUtils.requestQuotation(rfqData) : QuotationUtils.requestGuestQuotation(rfqData),
      onSuccess: () => {
         nextStep();
      }
   });

   const selectedColor = useRequestForQuotationColor({
      colors,
      colorName: color?.colorName,
      producerColorCode: color?.producerColorCode
   });

   const [selectedUomId, setSelectedUomId] = useState<string | undefined>(() => {
      if (userPermissions.hasVerifiedPermission) return undefined;
      return getDefaultChannelUomId(channelUOMs);
   });

   const stepRef = useRef<RequestForQuotationStep>(null);

   const uom = useMemo(() => {
      if (userPermissions.hasVerifiedPermission) {
         if (!customer?.preferences) return 'kg';
         const uomPreference = customer.preferences.find((preference) => preference.name === 'UOM_SYSTEM')?.value;
         return uomPreference === 'US customary units' ? 'lb' : 'kg';
      }
      return mapUomIdToUnit(selectedUomId ?? getDefaultChannelUomId(channelUOMs));
   }, [customer, userPermissions.hasVerifiedPermission, selectedUomId, channelUOMs]);

   const [quotationDetailsFormValue, setQuotationDetailsFormValue] = useState<QuotationDetailsFormValue | undefined>();
   const [verifyEmailFormValue, setVerifyEmailFormValue] = useState<VerifyEmailFormValue | undefined>();
   const [addressDetailsFormValue, setAddressDetailsFormValue] = useState<AddressDetailsFormValue | undefined>();
   const [deliveryAreaFormValue, setDeliveryAreaFormValue] = useState<DeliveryAreaFormValue | undefined>();
   const [contactDetailsFormValue, setContactDetailsFormValue] = useState<ContactDetailsFormValue | undefined>();
   const [summaryFormValue, setSummaryFormValue] = useState<SummaryFormValue | undefined>();

   const [requestQuoteError, setRequestQuoteError] = useState(false);

   const resetValues = () => {
      setQuotationDetailsFormValue(undefined);
      setVerifyEmailFormValue(undefined);
      setAddressDetailsFormValue(undefined);
      setDeliveryAreaFormValue(undefined);
      setContactDetailsFormValue(undefined);
   };

   const trackEvent = (event: TrackingEvents) => {
      TrackingUtils.track(event, {
         productName: productDescription,
         productId,
         userLoggedIn: JSON.stringify(!!user),
         userType: getUserType(user)
      });
   };

   const handleCloseFlyout = () => {
      resetSteps();
      onClose();
   };

   const handleCloseComplete = () => {
      resetValues();
      onComplete();
   };

   const handleTryAgain = () => {
      requestQuotationMutation.reset();
      previousStep();
   };

   const createQuotationLines = (colorId: number) => {
      if (!quotationDetailsFormValue) {
         getLogger().warn("Can't create quotation lines because form value is undefined");
         return [];
      }

      return quotationDetailsFormValue.quantityBreaks.map((qty) => ({
         productId: Number(productId),
         quantity: {
            amount: Number(qty.value),
            unitOfMeasure: uom
         },
         preferredPackagingGroupId: Number(quotationDetailsFormValue.packaging?.id),
         otherPackagingInfo: quotationDetailsFormValue.packagingDescription ?? '',
         colorId,
         desiredColor: color?.desiredColor,
         producerColorCode: color?.producerColorCode
      }));
   };

   const createVerifiedRequest = (additionalInfo: string, colorId: number): VerifiedRequestForQuotation | undefined => {
      if (!addressDetailsFormValue) {
         getLogger().warn("Can't request quote because address form value is undefined");
         return undefined;
      }

      return {
         type,
         preferredDeliveryTimeframe: addressDetailsFormValue.timeframe.id as PreferredDeliveryTimeframe,
         additionalInfo,
         deliveryAddressId: addressDetailsFormValue.deliveryAddress.id,
         quotationRequestLines: createQuotationLines(colorId)
      };
   };

   const createGuestRequest = (additionalInfo: string, colorId: number): GuestRequestForQuotation | undefined => {
      if (!deliveryAreaFormValue || !contactDetailsFormValue) {
         getLogger().warn("Can't request quote because form value is undefined");
         return undefined;
      }
      return {
         preferredDeliveryTimeframe: deliveryAreaFormValue.timeFrame.id as PreferredDeliveryTimeframe,
         additionalInfo,
         deliveryAddress: {
            country: deliveryAreaFormValue.country.value,
            city: deliveryAreaFormValue.city,
            postalCode: deliveryAreaFormValue.postalCode
         },
         contactInfo: {
            firstName: contactDetailsFormValue.firstName,
            lastName: contactDetailsFormValue.lastName,
            email: contactDetailsFormValue.email,
            language: languageCode,
            phoneNumber: `+${contactDetailsFormValue.phone.countryInfo?.prefix}${contactDetailsFormValue.phone.phone}`,
            company: {
               name: contactDetailsFormValue.companyName,
               countryIsoCode: contactDetailsFormValue.country.id
            }
         },
         quotationRequestLines: createQuotationLines(colorId)
      };
   };

   const onQuotationDetailsSubmit = (value: QuotationDetailsFormValue) => {
      trackEvent('rfq_click_continue_flyout');
      setQuotationDetailsFormValue(value);
      nextStep();
   };

   const onAddressDetailsSubmit = (value: AddressDetailsFormValue) => {
      setAddressDetailsFormValue(value);
      nextStep();
   };

   const onVerifyEmailSubmit = async (value: VerifyEmailFormValue) => {
      setVerifyEmailFormValue(value);
      const { email } = value;
      const userExists = await UserUtils.checkIfUserExists(email);
      nextStep({ userExists });
   };

   const onContactDetailsSubmit = (value: ContactDetailsFormValue) => {
      trackEvent('rfq_click_delivery_flyout');
      setContactDetailsFormValue(value);
      nextStep();
   };

   const onDeliveryAreaSubmit = (value: DeliveryAreaFormValue) => {
      setDeliveryAreaFormValue(value);
      nextStep();
   };

   const onSummarySubmit = async (value: SummaryFormValue) => {
      trackEvent('rfq_click_request_quote_flyout');
      let colorId = selectedColor?.id;

      if (!colorId && type === 'QUICK_QUOTE') {
         colorId = -1; // fallback to "Other"
      }

      if (!colorId) {
         getLogger().warn("Can't create request because colorId is undefined");
         setRequestQuoteError(true);
         nextStep();
         return;
      }

      const request =
         !user || user.groups.includes(UserGroups.digitalPlatformGuest) || user.groups.includes(UserGroups.digitalPlatformPendingVerification)
            ? createGuestRequest(value.additionalInfo, colorId)
            : createVerifiedRequest(value.additionalInfo, colorId);

      if (!request) {
         getLogger().warn("Can't create request because required data is missing");
         setRequestQuoteError(true);
         nextStep();
         return;
      }

      requestQuotationMutation.mutate(request);
   };

   const onNextStep = async () => {
      if (currentStep === RfqStep.RECOGNIZED_EMAIL) {
         trackEvent('rfq_click_login_button_flyout');

         const params = new URLSearchParams(searchParams.toString());
         if (quotationDetailsFormValue) {
            params.set('flyout', 'open');
            params.set('packaging', quotationDetailsFormValue.packaging.id);
            if (quotationDetailsFormValue.packagingDescription) {
               params.set('packaging-description', quotationDetailsFormValue.packagingDescription);
            }
            params.set('quantities', quotationDetailsFormValue.quantityBreaks.map((v) => v.value).join('-'));
         }

         login(`${pathname}?${params.toString()}`);
         return;
      }

      if (!stepRef.current) return;
      await stepRef.current.submit();
   };

   const onPreviousStep = () => {
      if (currentStep === RfqStep.RECOGNIZED_EMAIL) {
         trackEvent('rfq_click_back_button_flyout');
      }

      previousStep();
   };

   const handleCloseFinalStep = () => {
      resetValues();
      resetSteps();
      onClose?.();
   };

   const getNextLabel = () => {
      switch (currentStep) {
         case RfqStep.QUOTATION_DETAILS:
            return translate('page-consumer.request-for-quotation.delivery-details');
         case RfqStep.VERIFY_EMAIL:
            return translate('page-consumer.request-for-quotation.continue');
         case RfqStep.RECOGNIZED_EMAIL:
            return translate('page-consumer.request-for-quotation.login');
         case RfqStep.CONTACT_DETAILS:
            return translate('page-consumer.request-for-quotation.delivery-details');
         case RfqStep.ADDRESS_DETAILS:
            return translate('page-consumer.request-for-quotation.quote-summary');
         case RfqStep.DELIVERY_AREA:
            return translate('page-consumer.request-for-quotation.quote-summary');
         case RfqStep.SUMMARY:
            return translate('page-consumer.request-for-quotation.send-quote-request');
         default:
            return undefined;
      }
   };

   const getPreviousLabel = () => {
      switch (currentStep) {
         case RfqStep.VERIFY_EMAIL:
            return translate('page-consumer.request-for-quotation.quote-details');
         case RfqStep.RECOGNIZED_EMAIL:
            return translate('page-consumer.request-for-quotation.back');
         case RfqStep.CONTACT_DETAILS:
            return translate('page-consumer.request-for-quotation.quote-details');
         case RfqStep.ADDRESS_DETAILS:
            return translate('page-consumer.request-for-quotation.contact-details');
         case RfqStep.DELIVERY_AREA:
            return translate('page-consumer.request-for-quotation.contact-details');
         case RfqStep.SUMMARY:
            return translate('page-consumer.request-for-quotation.delivery-details');
         default:
            return undefined;
      }
   };

   return (
      <Flyout
         usePortal
         disableGutters
         position={phone ? 'bottom' : 'right'}
         width="100%"
         open={open}
         closeOnOutsideClick
         onFlyoutClose={() => handleCloseFlyout()}
      >
         <Box direction="column" height="100%" gap="none">
            <Box justify="between" items="center" spacing="lg">
               <Typography component="h4" variant="heading-4" color="strong">
                  {translate('page-consumer.request-for-quotation.request-a-quote')}
               </Typography>
               <IconButton icon={phone ? 'chevron-down' : 'chevron-right'} ariaLabel="close" onClick={() => handleCloseFlyout()} />
            </Box>

            <BoxItem grow={1} overflowY="auto">
               <Box spacing={{ x: 'lg' }} direction="column" gap="md">
                  {loading ? (
                     <Box justify="center">
                        <Spinner />
                     </Box>
                  ) : (
                     <>
                        {[RfqStep.QUOTATION_DETAILS, RfqStep.ADDRESS_DETAILS, RfqStep.DELIVERY_AREA, RfqStep.SUMMARY].includes(currentStep) && (
                           <>
                              <ProductFlyoutProductTitle title={productDescription} />
                              {selectedColor && (
                                 <ProductFlyoutColorDetails
                                    colorName={selectedColor.colorName}
                                    producerColorCode={selectedColor.producerColorCode}
                                    desiredColor={color?.desiredColor}
                                 />
                              )}
                           </>
                        )}
                        {currentStep === RfqStep.QUOTATION_DETAILS && (
                           <RfqFlyoutStepQuotationDetails
                              stepRef={stepRef}
                              uom={uom}
                              packagingGroups={packagingGroups ?? []}
                              leadTimeIndicators={leadTimeIndicators}
                              selectedColor={color}
                              isLeadTimeEnabled={leadTimeEnabled}
                              channelUOMs={channelUOMs}
                              canChangeUom={!userPermissions.hasVerifiedPermission && (channelUOMs?.length ?? 0) > 1}
                              selectedUomId={selectedUomId ?? getDefaultChannelUomId(channelUOMs)}
                              onUomChange={(newUomId: string) => {
                                 setSelectedUomId(newUomId);
                                 setQuotationDetailsFormValue(undefined);
                              }}
                              initialFormValue={getInitialQuotationDetailsFormValue(searchParams, packagingGroups ?? [])}
                              defaultFormValue={quotationDetailsFormValue}
                              onSubmit={onQuotationDetailsSubmit}
                           />
                        )}
                        {currentStep === RfqStep.VERIFY_EMAIL && (
                           <RfqFlyoutStepVerifyEmail stepRef={stepRef} defaultFormValue={verifyEmailFormValue} onSubmit={onVerifyEmailSubmit} />
                        )}
                        {currentStep === RfqStep.RECOGNIZED_EMAIL && <RfqFlyoutStepRecognizedEmail />}
                        {currentStep === RfqStep.ADDRESS_DETAILS && (
                           <RfqFlyoutStepAddressDetails
                              stepRef={stepRef}
                              addresses={customer!.addresses}
                              defaultFormValue={addressDetailsFormValue}
                              onSubmit={onAddressDetailsSubmit}
                              leadTimeIndicators={leadTimeIndicators}
                              selectedColor={color}
                              packagingGroupId={quotationDetailsFormValue?.packaging?.id}
                              isLeadTimeEnabled={leadTimeEnabled}
                           />
                        )}
                        {currentStep === RfqStep.DELIVERY_AREA && (
                           <RfqFlyoutStepDeliveryArea
                              stepRef={stepRef}
                              countries={countries}
                              defaultFormValue={deliveryAreaFormValue}
                              leadTimeIndicators={leadTimeIndicators}
                              selectedColor={color}
                              packagingGroupId={quotationDetailsFormValue?.packaging?.id}
                              isLeadTimeEnabled={leadTimeEnabled && !userPermissions.hasAnonymousPermission}
                              onSubmit={onDeliveryAreaSubmit}
                           />
                        )}
                        {currentStep === RfqStep.CONTACT_DETAILS && (
                           <RfqFlyoutStepContactDetails
                              stepRef={stepRef}
                              countries={countries}
                              email={verifyEmailFormValue?.email}
                              defaultFormValue={contactDetailsFormValue}
                              onSubmit={onContactDetailsSubmit}
                           />
                        )}
                        {currentStep === RfqStep.SUMMARY && (
                           <RfqFlyoutStepSummary
                              stepRef={stepRef}
                              setCurrentStep={setCurrentStep}
                              customer={customer}
                              userPermissions={userPermissions}
                              uom={uom}
                              quotationDetailsFormValue={quotationDetailsFormValue}
                              addressDetailsFormValue={addressDetailsFormValue}
                              deliveryAreaFormValue={deliveryAreaFormValue}
                              contactDetailsFormValue={contactDetailsFormValue}
                              summaryFormValue={summaryFormValue}
                              onSummaryChange={setSummaryFormValue}
                              onSubmit={onSummarySubmit}
                              leadTimeEnabled={leadTimeEnabled && !userPermissions.hasAnonymousPermission}
                              leadTimeIndicators={leadTimeIndicators}
                              selectedColor={color}
                           />
                        )}
                        {currentStep === RfqStep.FINAL_MESSAGE && (
                           <RfqFlyoutStepFinalMessage requestQuoteError={requestQuotationMutation.data === false || requestQuoteError} />
                        )}
                     </>
                  )}
               </Box>
            </BoxItem>

            <Box spacing="md" gap="xs" direction="column" items="center">
               {userPermissions.hasVerifiedPermission && currentStep === RfqStep.FINAL_MESSAGE && from === 'PRODUCT_DETAILS' && (
                  <Link underline="none" href={`/my/${languageCode}/my-prices`} prefetch={false} fullWidth>
                     <Button variant="primary" fullWidth onClick={() => trackEvent('rfq_click_my_quotes_flyout')}>
                        {translate('page-consumer.request-for-quotation.my-quotes')}
                     </Button>
                  </Link>
               )}
               {currentStep !== RfqStep.FINAL_MESSAGE && (
                  <Button onClick={onNextStep} fullWidth trailingIcon="arrow-right" disabled={requestQuotationMutation.isPending}>
                     {getNextLabel()}
                  </Button>
               )}
               {currentStep === RfqStep.QUOTATION_DETAILS && (
                  <Button variant="secondary" onClick={handleCloseComplete} fullWidth>
                     {from === 'PRODUCT_DETAILS' ? translate('extended-color-picker.label') : translate('request-for-quotation.close')}
                  </Button>
               )}
               {currentStep === RfqStep.FINAL_MESSAGE && requestQuotationMutation.data === false && (
                  <Button variant="primary" fullWidth onClick={handleTryAgain} trailingIcon={!phone ? 'refresh' : ''}>
                     {translate('page-consumer.request-for-quotation.try-again')}
                  </Button>
               )}
               {currentStep === RfqStep.FINAL_MESSAGE && (
                  <Button variant="secondary" onClick={handleCloseFinalStep} fullWidth>
                     {translate('request-for-quotation.close')}
                  </Button>
               )}
               {currentStep !== RfqStep.QUOTATION_DETAILS && currentStep !== RfqStep.FINAL_MESSAGE && (
                  <Button variant="secondary" onClick={onPreviousStep} fullWidth leadingIcon="arrow-left">
                     {getPreviousLabel()}
                  </Button>
               )}
               {currentStep !== RfqStep.FINAL_MESSAGE && <DotsStepper numberOfSteps={stepCount} currentStep={currentStepIndex} readOnly />}
            </Box>
         </Box>
      </Flyout>
   );
};
